
import { api } from '../core';
import { localDB } from '../helpers';
import { UsageEvent, UserStatistics, User, ServiceDefinition } from '../../types';
import { SERVICES_DATA } from '../../constants';

const MOCK_EVENTS: UsageEvent[] = [];

// Helper to determine time saved based on action
const calculateTimeSaved = (type: UsageEvent['type'], targetId?: string): number => {
  if (type === 'message') return 1; 
  if (type === 'service_run') return 15; 
  if (type === 'flow_execution') return 5;
  return 0;
};

const PLAN_LIMITS = {
  free: { messages: 15, flows: false, proServices: false, agents: 1 },
  pro: { messages: Infinity, flows: true, proServices: true, agents: 10 },
  enterprise: { messages: Infinity, flows: true, proServices: true, agents: 50 }
};

export const UsageService = {
  
  trackEvent: async (type: UsageEvent['type'], targetId?: string): Promise<void> => {
    const timeSaved = calculateTimeSaved(type, targetId);
    
    // Map to UsageEventCreateVM
    const payload = {
        eventType: type,
        targetId: targetId,
        targetType: type === 'service_run' ? 'service' : 'system',
        timeSavedMinutes: timeSaved,
        timestamp: new Date().toISOString(),
        tokensUsed: Math.floor(Math.random() * 100) // Mock token usage calc
    };

    return api.execute<void>(
      '/api/V1/UsageEvent/Create',
      async () => {
        const user = localDB.get<User | null>('lahja_user', null);
        if (!user) return;

        const newEvent: UsageEvent = {
          id: Date.now().toString(),
          userId: user.id,
          type,
          targetId,
          timestamp: Date.now(),
          timeSavedMinutes: timeSaved
        };
        
        const events = localDB.get<UsageEvent[]>('lahja_usage_events', MOCK_EVENTS);
        localDB.set('lahja_usage_events', [...events, newEvent]);
      },
      { method: 'POST', body: payload }
    );
  },

  getStats: async (): Promise<UserStatistics> => {
    return api.execute<UserStatistics>(
      '/api/V1/UsageEvent/GetAllByLanguage', // Using fetch all to aggregate on client
      async () => {
        const events = localDB.get<UsageEvent[]>('lahja_usage_events', MOCK_EVENTS);
        return aggregateStats(events);
      }
    ).then(res => {
        // If we received raw events list from API, aggregate it
        if (Array.isArray(res)) {
            const events: UsageEvent[] = res.map((e: any) => ({
                id: e.id,
                userId: e.customerId,
                type: e.eventType as any,
                targetId: e.targetId,
                timestamp: new Date(e.timestamp).getTime(),
                timeSavedMinutes: e.timeSavedMinutes || 0
            }));
            return aggregateStats(events);
        }
        return res; 
    });
  },

  checkPermission: async (action: 'message' | 'create_flow' | 'run_service' | 'create_agent', resource?: any): Promise<{ allowed: boolean; reason?: string }> => {
    // This is typically a local check for responsiveness, but can verify against server plan
    return api.execute<{ allowed: boolean; reason?: string }>(
        '/api/mock/usage/check',
        async () => {
            const user = localDB.get<User | null>('lahja_user', null);
            if (!user) return { allowed: false, reason: 'User not found' };

            const plan = user.plan || 'free';
            const limits = PLAN_LIMITS[plan];
            
            const events = localDB.get<UsageEvent[]>('lahja_usage_events', MOCK_EVENTS);

            if (action === 'message') {
                if (limits.messages === Infinity) return { allowed: true };
                const today = new Date().setHours(0,0,0,0);
                const msgsToday = events.filter(e => e.type === 'message' && e.timestamp >= today).length;
                if (msgsToday >= limits.messages) return { allowed: false, reason: `Daily limit reached (${limits.messages}). Upgrade required.` };
            }

            if (action === 'run_service') {
                const service = resource as ServiceDefinition;
                if (service?.isPro && !limits.proServices) return { allowed: false, reason: 'Pro service. Upgrade required.' };
            }

            if (action === 'create_flow') {
                if (!limits.flows) return { allowed: false, reason: 'Automation available in Pro/Enterprise plans.' };
            }
            
            if (action === 'create_agent') {
                const agents = localDB.get<any[]>('lahja_agents', []); 
                if (agents.length >= limits.agents) return { allowed: false, reason: `Agent limit reached (${limits.agents}).` };
            }
            
            return { allowed: true };
        }
    );
  }
};

const aggregateStats = (events: UsageEvent[]): UserStatistics => {
    const totalRequests = events.length;
    const totalTimeSaved = events.reduce((acc, curr) => acc + curr.timeSavedMinutes, 0);
    const uniqueServices = new Set(events.filter(e => e.type === 'service_run' && e.targetId).map(e => e.targetId as string));
    
    const activityHistory = Array(7).fill(0);
    const now = new Date();
    events.forEach(e => {
        const eventDate = new Date(e.timestamp);
        const diffTime = Math.abs(now.getTime() - eventDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        if (diffDays <= 7) activityHistory[7 - diffDays]++; 
    });

    const distMap: Record<string, number> = {};
    events.forEach(e => {
        let cat = 'Chat';
        if (e.type === 'service_run' && e.targetId) {
            const svc = SERVICES_DATA.find(s => s.id === e.targetId);
            cat = svc?.category || 'Service';
        } else if (e.type === 'flow_execution') cat = 'Automation';
        distMap[cat] = (distMap[cat] || 0) + 1;
    });

    const usageByService = Object.entries(distMap).map(([label, count], idx) => ({
        label,
        count: Math.round((count / (totalRequests || 1)) * 100),
        color: idx % 2 === 0 ? 'bg-emerald-500' : 'bg-blue-500'
    }));

    return {
        totalRequests,
        totalTimeSaved: Math.floor(totalTimeSaved / 60),
        activeServices: uniqueServices.size,
        efficiencyScore: Math.min(100, Math.max(50, totalRequests / 5)),
        usageByService,
        activityHistory,
        usedServiceIds: Array.from(uniqueServices)
    };
};
